import React from 'react';
import { Box, Typography } from '@mui/material';
import { isValidElement } from 'react';
import { isHelpContent, type HelpContent, type HelpValue } from './helpModel';

export interface RichTooltipContentProps extends Omit<HelpContent, 'kind'> {
  kind?: HelpContent['kind'];
}

const sectionSx = {
  display: 'block',
  lineHeight: 1.55,
};

const RichTooltipContent: React.FC<RichTooltipContentProps> = ({
  kind = 'field',
  title,
  summary,
  why,
  downstream,
  note,
  prerequisite,
  shortcut,
  blockedReason,
  nextStep,
  density = 'rich',
}) => {
  const isCompact = density === 'compact';
  const sectionFontSize = isCompact ? '0.74rem' : '0.76rem';

  return (
    <Box sx={{ display: 'grid', gap: isCompact ? 0.7 : 0.9, maxWidth: isCompact ? 300 : 340 }}>
      <Typography
        component="div"
        sx={{
          fontSize: isCompact ? '0.78rem' : '0.82rem',
          fontWeight: 700,
          color: 'common.white',
          lineHeight: 1.35,
        }}
      >
        {title}
      </Typography>
      <Typography component="div" sx={{ ...sectionSx, fontSize: isCompact ? '0.75rem' : '0.77rem', color: 'grey.100' }}>
        {summary}
      </Typography>
      {why && (
        <Typography component="div" sx={{ ...sectionSx, fontSize: sectionFontSize, color: 'grey.300' }}>
          <Box component="span" sx={{ fontWeight: 700, color: 'primary.light' }}>
            {kind === 'step' ? 'Why this step:' : 'Why it matters:'}
          </Box>{' '}
          {why}
        </Typography>
      )}
      {downstream && (
        <Typography component="div" sx={{ ...sectionSx, fontSize: sectionFontSize, color: 'grey.300' }}>
          <Box component="span" sx={{ fontWeight: 700, color: 'info.light' }}>
            {kind === 'action' ? 'Result:' : 'Downstream effect:'}
          </Box>{' '}
          {downstream}
        </Typography>
      )}
      {prerequisite && (
        <Typography component="div" sx={{ ...sectionSx, fontSize: sectionFontSize, color: 'grey.300' }}>
          <Box component="span" sx={{ fontWeight: 700, color: 'secondary.light' }}>
            Before you use it:
          </Box>{' '}
          {prerequisite}
        </Typography>
      )}
      {shortcut && (
        <Typography component="div" sx={{ ...sectionSx, fontSize: sectionFontSize, color: 'grey.300' }}>
          <Box component="span" sx={{ fontWeight: 700, color: 'success.light' }}>
            Shortcut:
          </Box>{' '}
          {shortcut}
        </Typography>
      )}
      {blockedReason && (
        <Typography component="div" sx={{ ...sectionSx, fontSize: sectionFontSize, color: 'warning.light' }}>
          <Box component="span" sx={{ fontWeight: 700 }}>
            Blocked because:
          </Box>{' '}
          {blockedReason}
        </Typography>
      )}
      {nextStep && (
        <Typography component="div" sx={{ ...sectionSx, fontSize: sectionFontSize, color: 'grey.300' }}>
          <Box component="span" sx={{ fontWeight: 700, color: 'success.light' }}>
            To enable it:
          </Box>{' '}
          {nextStep}
        </Typography>
      )}
      {note && (
        <Typography component="div" sx={{ ...sectionSx, fontSize: isCompact ? '0.72rem' : '0.74rem', color: 'warning.light' }}>
          <Box component="span" sx={{ fontWeight: 700 }}>
            Watch for:
          </Box>{' '}
          {note}
        </Typography>
      )}
    </Box>
  );
};

export const renderHelpContent = (content: HelpValue) => {
  if (!content) {
    return '';
  }

  if (typeof content === 'string' || isValidElement(content)) {
    return content;
  }

  if (isHelpContent(content)) {
    return <RichTooltipContent {...content} />;
  }

  return '';
};

export const createRichTooltipContent = (props: RichTooltipContentProps) => <RichTooltipContent {...props} />;

export default RichTooltipContent;
